import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Plus, Calendar, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const TrainingManagement = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isFranchiseeRole = ['FRANCHISEE', 'FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR'].includes(user?.role);
  const isHQAdmin = user?.role === 'HQ_ADMIN';
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    venue: '',
    max_participants: 0,
    online_link: ''
  });
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    date: '',
    venue: '',
    max_participants: 0,
    online_link: ''
  });
  const [attendanceData, setAttendanceData] = useState({
    staff_name: ''
  });

  useEffect(() => {
    fetchEvents();
  }, [token]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/training/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch training events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/training/events`, {
        ...formData,
        created_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training event created successfully');
      setOpenDialog(false);
      fetchEvents();
      setFormData({ title: '', description: '', date: '', venue: '', max_participants: 0, online_link: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleAttendance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/training/attendance`, {
        training_id: selectedEvent.training_id,
        franchisee_id: user.franchisee_id,
        staff_name: attendanceData.staff_name,
        recorded_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attendance recorded successfully');
      setAttendanceDialog(false);
      fetchEvents();
      setAttendanceData({ staff_name: '' });
    } catch (error) {
      toast.error('Failed to record attendance');
    }
  };

  const openAttendanceDialog = (event) => {
    setSelectedEvent(event);
    setAttendanceDialog(true);
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setEditFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      venue: event.venue,
      max_participants: event.max_participants,
      online_link: event.online_link || ''
    });
    setEditDialog(true);
  };

  const openDeleteDialog = (event) => {
    setSelectedEvent(event);
    setDeleteDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/training/events/${selectedEvent.training_id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training event updated successfully');
      setEditDialog(false);
      fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/training/events/${selectedEvent.training_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training event deleted successfully');
      setDeleteDialog(false);
      fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="training-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Training & Development</h1>
            <p className="text-muted-foreground mt-1">Manage training events and attendance</p>
          </div>
          {isHQAdmin && (
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-training-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Event
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Training Event</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title*</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Description*</Label>
                    <Textarea rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="description-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Date*</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required data-testid="date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Venue*</Label>
                    <Input value={formData.venue} onChange={(e) => setFormData({...formData, venue: e.target.value})} required data-testid="venue-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Participants*</Label>
                    <Input type="number" value={formData.max_participants} onChange={(e) => setFormData({...formData, max_participants: parseInt(e.target.value)})} required data-testid="max-participants-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Online Link</Label>
                    <Input value={formData.online_link} onChange={(e) => setFormData({...formData, online_link: e.target.value})} data-testid="online-link-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-training-btn">Create Event</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <p className="text-center text-muted-foreground col-span-full">Loading...</p>
          ) : (
            events.map((event) => (
              <Card key={event.training_id} className="border border-border/60 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid={`training-card-${event.title}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <Calendar className="h-8 w-8 text-primary" />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{event.current_participants}/{event.max_participants} participants</span>
                      {isHQAdmin && (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => openEditDialog(event)}
                            data-testid={`edit-training-${event.title}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => openDeleteDialog(event)}
                            data-testid={`delete-training-${event.title}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <CardTitle className="mt-4">{event.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{event.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="font-medium">{event.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Venue:</span>
                      <span className="font-medium">{event.venue}</span>
                    </div>
                    {event.online_link && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Online Link:</span>
                        <a href={event.online_link} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline truncate max-w-[150px]">
                          Join Online
                        </a>
                      </div>
                    )}
                  </div>
                  {isFranchiseeRole && (
                    <Button className="w-full mt-4" onClick={() => openAttendanceDialog(event)} data-testid={`record-attendance-${event.title}`}>
                      Record Attendance
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Attendance Dialog */}
        <Dialog open={attendanceDialog} onOpenChange={setAttendanceDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Attendance</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAttendance} className="space-y-4">
              <div className="space-y-2">
                <Label>Staff Name*</Label>
                <Input value={attendanceData.staff_name} onChange={(e) => setAttendanceData({...attendanceData, staff_name: e.target.value})} required data-testid="staff-name-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAttendanceDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-attendance-btn">Record Attendance</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Training Event Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Training Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Title*</Label>
                <Input 
                  value={editFormData.title} 
                  onChange={(e) => setEditFormData({...editFormData, title: e.target.value})} 
                  required 
                  data-testid="edit-title-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Description*</Label>
                <Textarea 
                  rows={3} 
                  value={editFormData.description} 
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})} 
                  required 
                  data-testid="edit-description-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Date*</Label>
                <Input 
                  type="date" 
                  value={editFormData.date} 
                  onChange={(e) => setEditFormData({...editFormData, date: e.target.value})} 
                  required 
                  data-testid="edit-date-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Venue*</Label>
                <Input 
                  value={editFormData.venue} 
                  onChange={(e) => setEditFormData({...editFormData, venue: e.target.value})} 
                  required 
                  data-testid="edit-venue-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Max Participants*</Label>
                <Input 
                  type="number" 
                  value={editFormData.max_participants} 
                  onChange={(e) => setEditFormData({...editFormData, max_participants: parseInt(e.target.value)})} 
                  required 
                  data-testid="edit-max-participants-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Online Link</Label>
                <Input 
                  value={editFormData.online_link} 
                  onChange={(e) => setEditFormData({...editFormData, online_link: e.target.value})} 
                  data-testid="edit-online-link-input" 
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-edit-training-btn">Save Changes</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Training Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to delete the training event "{selectedEvent?.title}"? 
                This action cannot be undone and will also remove all associated attendance records.
              </p>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleDelete}
                  data-testid="confirm-delete-training-btn"
                >
                  Delete Event
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TrainingManagement;